import { NgModule, ModuleWithProviders, ErrorHandler, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { LoggerService } from './services/logger.service';
import { PerformanceLoggerService } from './services/performance-logger.service';
import { LoggerFactoryService, LoggingModuleConfig } from './services/logger-factory.service';
import { LoggingInterceptor } from './interceptors/logging.interceptor';
import { ConsoleTransport } from './transports/console.transport';
import { HttpTransport } from './transports/http.transport';
import { LocalStorageTransport } from './transports/local-storage.transport';
import { LOGGER_CONFIG } from './config/logger.tokens';
import { ErrorDeduplicationService } from '../error-deduplication.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private logger: LoggerService,
    private errorDeduplicationService: ErrorDeduplicationService
  ) {}

  handleError(error: any): void {
    // Check if this error should be logged (deduplication check)
    if (!this.errorDeduplicationService.shouldLogError(error, 'GlobalErrorHandler')) {
      // Error is being throttled - don't log it
      return;
    }

    // Extract meaningful error information
    const errorMessage = this.getErrorMessage(error);
    
    // Log the error
    this.logger.critical('Unhandled error occurred', error, {
      originalError: error?.originalError || error,
      rejection: error?.rejection,
      zone: error?.zone,
      task: error?.task
    }, 'GlobalErrorHandler');
  }

  private getErrorMessage(error: any): string {
    if (error?.message) return error.message;
    if (error?.originalError?.message) return error.originalError.message;
    if (typeof error === 'string') return error;
    return 'Unknown error occurred';
  }
}

@NgModule({
  imports: [CommonModule, HttpClientModule],
  providers: [
    LoggerService,
    PerformanceLoggerService,
    LoggerFactoryService,
    LoggingInterceptor,
    ErrorDeduplicationService
  ]
})
export class LoggingModule {
  static forRoot(config?: LoggingModuleConfig): ModuleWithProviders<LoggingModule> {
    const moduleConfig: LoggingModuleConfig = {
      environment: 'development',
      enableConsole: true,
      enableHttp: false,
      enableLocalStorage: false,
      httpEndpoint: '/api/ssr/logs',
      enableInterceptor: true,
      ...config
    };

    return {
      ngModule: LoggingModule,
      providers: [
        // Use factory service to create logger config with proper dependency injection
        {
          provide: LOGGER_CONFIG,
          useFactory: (loggerFactory: LoggerFactoryService) => {
            return loggerFactory.createLoggerConfig(moduleConfig);
          },
          deps: [LoggerFactoryService]
        },
        // Global error handler
        {
          provide: ErrorHandler,
          useClass: GlobalErrorHandler
        },
        // HTTP interceptor (optional)
        ...(moduleConfig.enableInterceptor ? [{
          provide: HTTP_INTERCEPTORS,
          useClass: LoggingInterceptor,
          multi: true
        }] : [])
      ]
    };
  }
}